import { PencilIcon } from "@heroicons/react/solid";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { Link } from "react-router-dom";
import { useOne, useParse } from "@refinedev/core";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CollapsibleSection } from "components/collapsibleSection";
import { ClockIcon, DocumentTextIcon, PhotographIcon, UserGroupIcon } from "@heroicons/react/outline";
import { IHomepageDetailData } from "interfaces";
import { Fire } from "components/customIcons/fire";
import { Fragment, useState } from "react";
import { HistoryLodModal } from "components/historyLogModal";
import { isHaveActionPermission } from "utills/helpers";

export const HomepageDetail: React.FC = () => {
  const [openLogModalId, setOpenLogModalId] = useState<string | null>(null);

  const parse = useParse();
  const { params } = parse();

  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IHomepageDetailData>({
    resource: "content/homepage/detail",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const _handleOpenHistoryLog = (e: any, id: string) => {
    e.stopPropagation();
    setOpenLogModalId(id);
  }

  const homepageData = data?.data;

  if (isLoading) {
    return <FullScreenLoading />
  }
  
  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Homepage Detail"
        {...(isHaveActionPermission("homepage", "mkt", "edit") && {
          additionalLabel: (
            <Link to={`/mkt/homepage/edit`}>
              <Button>
                <span className="sr-only">Edit</span>
                <PencilIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Edit</span>
              </Button>
            </Link>
          ),
        })}
      />
      <CollapsibleSection 
        label="Banner Information"
        defaultOpen={params?.default === "banner"}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, "ads-banner")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem 
            icon={PhotographIcon}
            label="Top Banner"
            content={(
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
                {(homepageData?.["ads-banner"]["top-banner"].data ?? []).map((banner, index) => (
                  <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                    <div className="flex flex-col gap-y-4">
                      <div>
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                          Desktop Banner #{index + 1}
                        </div>
                        <div className="overflow-hidden rounded-b-lg">
                          <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                        </div>
                      </div>
                      {banner.mobile_banner && (
                        <div>
                          <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                            Mobile Banner #{index + 1}
                          </div>
                          <div className="overflow-hidden rounded-b-lg">
                            <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={PhotographIcon}
            label="Hero banner"
            content={(
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
                {homepageData?.["ads-banner"]["hero-banner"].data.map((banner, index) => (
                  <Fragment key={index}>
                    {banner.type === "image" ? (
                      <div className="col-span-full sm:col-span-3 lg:col-span-2" key={index}>
                        <img src={banner.banner} className="w-full" alt={`Hero Banner`} />
                        {banner.open_url ? (
                          <div className="mt-1">
                            <Link to={banner.open_url} target="_blank" className="text-sm font-medium text-brand-500 hover:underline">
                              {banner.open_url}
                            </Link>
                          </div>
                        ) : <></>}
                      </div>
                    ) : (
                      <div className="col-span-full">
                        <Link to={banner.banner} target="_blank" className="text-sm font-medium text-brand-500 hover:underline">
                          {banner.banner}
                        </Link>
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={PhotographIcon}
            label="Slide banner"
            content={(
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
                {(homepageData?.["ads-banner"]["slide-banner"].data ?? []).map((banner, index) => (
                  <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                    <div className="flex flex-col gap-y-4">
                      <div>
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                          Desktop Banner #{index + 1}
                        </div>
                        <div className="overflow-hidden rounded-b-lg">
                          <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                        </div>
                      </div>
                      {banner.mobile_banner && (
                        <div>
                          <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                            Mobile Banner #{index + 1}
                          </div>
                          <div className="overflow-hidden rounded-b-lg">
                            <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={PhotographIcon}
            label="Promotion banner"
            content={(
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
                {(homepageData?.["ads-banner"]["promotion-banner"].data ?? []).map((banner, index) => (
                  <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                    <div className="flex flex-col gap-y-4">
                      <div>
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                          Desktop Banner #{index + 1}
                        </div>
                        <div className="overflow-hidden rounded-b-lg">
                          <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                        </div>
                      </div>
                      {banner.mobile_banner && (
                        <div>
                          <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                            Mobile Banner #{index + 1}
                          </div>
                          <div className="overflow-hidden rounded-b-lg">
                            <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={PhotographIcon}
            label="Affiliate banner"
            content={(
              <div className="grid gap-x-4 gap-y-6 sm:grid-cols-12 lg:gap-x-6">
                {(homepageData?.["ads-banner"]["affiliate-banner"].data ?? []).map((banner, index) => (
                  <div key={index} className="sm:col-span-6 lg:col-span-4 xl:col-span-3">
                    <div className="flex flex-col gap-y-4">
                      <div>
                        <div className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                          Desktop Banner #{index + 1}
                        </div>
                        <div className="overflow-hidden rounded-b-lg">
                          <img src={banner.banner} alt="Banner" className="w-full h-auto" />
                        </div>
                      </div>
                      {banner.mobile_banner && (
                        <div>
                          <div className="bg-amber-100 text-amber-800 px-2 py-1 text-xs font-semibold rounded-t-lg">
                            Mobile Banner #{index + 1}
                          </div>
                          <div className="overflow-hidden rounded-b-lg">
                            <img src={banner.mobile_banner} alt="Mobile Banner" className="w-full h-auto" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Event information"
        defaultOpen={params?.default === "event"}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, "hot-event")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem 
            icon={Fire}
            label="Hot events"
            content={(
              <div>
                {homepageData?.home["hot-event"].data.map((event, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {event.name} {!event.is_active ? "(Inactive)" : ""}
                  </p>
                ))}
              </div>
            )}
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Organizer information"
        defaultOpen={params?.default === "organizer"}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, "organizer")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem 
            icon={UserGroupIcon}
            label="Organizers"
            content={(
              <div>
                {homepageData?.home.organizer.data.map((organizer, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {organizer.name}
                  </p>
                ))}
              </div>
            )}
          />
        </div>
      </CollapsibleSection>
      <CollapsibleSection 
        label="Blogs"
        defaultOpen={params?.default === "blog"}
        toolbar={(
          <Button
            className="w-8 h-8 bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200 sm:w-auto"
            onClick={(e: any) => _handleOpenHistoryLog(e, "blog")}
          >
            <span className="sr-only">History Log</span>
            <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
            <span className="hidden sm:block sm:ml-2">Log</span>
          </Button>
        )}
      >
        <div className="divide-y divide-gray-100">
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="All"
            content={(
              <div>
                {homepageData?.blog.all.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="Promo"
            content={(
              <div>
                {homepageData?.blog.promo.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="News"
            content={(
              <div>
                {homepageData?.blog.news.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="Health & Wellness"
            content={(
              <div>
                {homepageData?.blog.health?.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="Blogs"
            content={(
              <div>
                {homepageData?.blog.blogs.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
          <DescriptionItem 
            icon={DocumentTextIcon}
            label="Others"
            content={(
              <div>
                {homepageData?.blog.others.data.map((blog, index) => (
                  <p className="text-sm leading-6" key={index}>
                    {blog.name}
                  </p>
                ))}
              </div>
            )}
          />
        </div>
      </CollapsibleSection>
      <HistoryLodModal
        resource={`content/homepage/${openLogModalId}/history`}
        open={openLogModalId ? true : false}
        onClose={() => setOpenLogModalId(null)}
        key="mkt-homepage"
        id="mkt-homepage"
      />
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}